import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { api, fmtNum, curSym, fmtDate, errMsg } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { PrintButton } from "@finance/components/PrintButton";
import { importChecklist } from "@finance/lib/importUtils";
import { exportToCsv } from "@finance/lib/exportCsv";
import { StatusBadge } from "@finance/components/StatusBadge";
import TransactionForm from "@finance/components/TransactionForm";
import Fulfillment from "@finance/components/Fulfillment";
import ImportDocuments from "@finance/components/ImportDocuments";
import UploadDialog from "@finance/components/UploadDialog";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@finance/components/ui/tabs";
import { Plus, DownloadSimple, MagnifyingGlass, ArrowLeft, Coins, Truck, Warning } from "@phosphor-icons/react";
import { TableEmpty, SectionCard, KpiStrip, KpiCard } from "@finance/components/DesignSystem";
import { MultiSelectFilter } from "@finance/components/MultiSelectFilter";
import { useStatusFlow } from "@finance/lib/useStatusFlow";
import { useAuth } from "@finance/context/AuthContext";
import { RequestAdvanceButton } from "@finance/components/AdvanceApply";
import { PoApprovalActions, PoApprovalBanner, PoApprovalPill } from "@finance/components/PoApproval";

const readSel = (key) => { try { return JSON.parse(localStorage.getItem(key)) || []; } catch { return []; } };

const FUL_TONE = {
  "Not Started": "badge-neutral",
  "Partially Received": "badge-warning",
  "Received": "badge-info",
  "Partially Delivered": "badge-warning",
  "Delivered": "badge-info",
  "Partially Invoiced": "badge-warning",
  "Invoiced by Supplier": "badge-success",
  "Completed": "badge-success",
};
const FulfillmentPill = ({ status }) => (
  status ? <span className={`badge ${FUL_TONE[status] || "badge-neutral"}`} data-testid={`txn-ful-${status?.replace(/\s+/g, "-").toLowerCase()}`}><span className="badge-dot" />{status}</span> : <span className="text-gray-300">—</span>
);

export default function Transactions() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [users, setUsers] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [filters, setFilters] = useState({ currency: "all", q: "" });
  const [statusSel, setStatusSel] = useState(() => readSel("lx.txn.statusSel"));
  useEffect(() => { localStorage.setItem("lx.txn.statusSel", JSON.stringify(statusSel)); }, [statusSel]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [pocOpen, setPocOpen] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [preview, setPreview] = useState(null);
  const [fulStats, setFulStats] = useState(null);
  const [adjStats, setAdjStats] = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const [delReason, setDelReason] = useState("");
  const [searchParams] = useSearchParams();
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const STATUS_FLOW = useStatusFlow();
  const { user } = useAuth();
  const isFinance = !!(user && (user.is_executive || user.role_code === "FM"));
  const [draftSummary, setDraftSummary] = useState({ total: 0, by_transaction: {}, stale_days: 3, stale_total: 0 });
  const [threshOpen, setThreshOpen] = useState(false);
  const [threshVal, setThreshVal] = useState("");

  const saveThreshold = async () => {
    try {
      const { data } = await api.put("/settings/supplier-draft-reminder", { stale_days: parseInt(threshVal, 10) });
      setDraftSummary((s) => ({ ...s, stale_days: data.stale_days }));
      setThreshOpen(false);
      toast.success(`Reminder window set to ${data.stale_days} day(s)`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const load = useCallback(() => {
    const params = {};
    if (filters.currency !== "all") params.currency = filters.currency;
    if (filters.q) params.q = filters.q;
    api.get("/transactions", { params }).then((r) => setRows(r.data));
    api.get("/supplier-invoice-drafts").then((r) => setDraftSummary(r.data)).catch(() => {});
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/currencies").then((r) => setCurrencies(r.data || []));
  }, []);

  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId) { navigate(`/transactions/${openId}`, { replace: true }); return; }
    if (routeId && routeId !== "new") openDetail(routeId); else setDetail(null);
    const tp = searchParams.get("tab");
    setActiveTab(tp || "overview");
    // eslint-disable-next-line
  }, [routeId, searchParams]);

  const openDetail = async (id) => {
    setFulStats(null); setAdjStats(null); setNotFound(false);
    try {
      const { data } = await api.get(`/transactions/${id}`);
      setDetail(data);
    } catch (e) { setNotFound(true); }
  };

  const uploadDoc = async (file) => {
    if (!file || !detail) return;
    setUploadingDoc(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("document_type", "Supporting_Document");
    fd.append("transaction_id", detail.id);
    fd.append("run_extraction", "false");
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document attached");
      openDetail(detail.id);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUploadingDoc(false); }
  };

  const previewDoc = async (d) => {
    setPreview({ doc: d, url: null });
    try {
      const resp = await api.get(`/files/${d.storage_path}`, { responseType: "blob" });
      setPreview({ doc: d, url: URL.createObjectURL(resp.data) });
    } catch { toast.error("Could not load preview"); }
  };

  const doDelete = async () => {
    if (!detail) return;
    if (!delReason.trim()) { toast.warning("Please enter a reason for deletion."); return; }
    try {
      await api.delete(`/transactions/${detail.id}`, { params: { reason: delReason.trim() } });
      toast.success("Somcore PO deleted");
      setDelOpen(false); setDelReason(""); navigate("/transactions");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const cols = [
    { key: "lx_po_no", label: "Somcore PO" },
    { key: "client_po_no", label: "Client PO" },
    { key: "supplier_name", label: "Supplier" },
    { key: "client_name", label: "Client" },
    { key: "currency", label: "Currency" },
    { key: "total_amount", label: "Total", format: (v) => fmtNum(v) },
    { key: "payable_amount", label: "Payable", format: (v) => fmtNum(v) },
    { key: "status", label: "Status" },
    { key: "fulfillment_status", label: "Fulfillment" },
    { key: "assigned_to_name", label: "Assigned" },
    { key: "eta", label: "ETA", format: fmtDate },
  ];

  if (routeId === "new") {
    return (
      <Layout>
        <PageHeader title="New Somcore PO" subtitle="Create a Somcore purchase order"
          actions={<Button variant="outline" data-testid="back-to-lynx-pos-new" onClick={() => navigate("/transactions")}><ArrowLeft size={16} className="mr-1" />Back to list</Button>} />
        <div className="p-8">
          <div className="mx-auto max-w-6xl rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <TransactionForm asPage open onOpenChange={() => navigate("/transactions")} initial={null}
              clients={clients} suppliers={suppliers} users={users}
              onSaved={(saved) => { load(); navigate(saved?.id ? `/transactions/${saved.id}` : "/transactions"); }} />
          </div>
        </div>
      </Layout>
    );
  }

  if (routeId) {
    return (
      <Layout>
        <PageHeader
          title={detail ? (detail.lx_po_no || detail.client_po_no || "Somcore PO") : "Somcore PO"}
          subtitle="Somcore PO record"
          actions={<Button variant="outline" data-testid="back-to-lynx-pos" onClick={() => navigate("/transactions")}><ArrowLeft size={16} className="mr-1" />Back to list</Button>}
        />
        <div className="p-8">
          {notFound ? (
            <div className="mx-auto max-w-lg rounded-sm border border-gray-200 bg-white p-8 text-center" data-testid="txn-detail-notfound">
              <div className="text-sm text-gray-500">This Somcore PO could not be found. It may have been deleted.</div>
              <Button className="mt-4" variant="outline" onClick={() => navigate("/transactions")}><ArrowLeft size={16} className="mr-1" />Back to list</Button>
            </div>
          ) : !detail ? (
            <div className="text-sm text-gray-400" data-testid="txn-detail-loading">Loading…</div>
          ) : (
            <div className="mx-auto max-w-6xl space-y-5" data-testid="txn-detail-page">
              <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-gray-200 bg-white px-5 py-4 shadow-sm">
                <div>
                  <div className="flex items-center gap-3">
                    <span className="font-display text-lg font-semibold">{detail.lx_po_no || detail.client_po_no || "Transaction"}</span>
                    <StatusBadge status={detail.status} />
                    {!detail.direct_sale && <PoApprovalPill status={detail.approval_status} testid="txn-approval-pill" />}
                  </div>
                  <div className="mt-1 text-xs text-gray-500" data-testid="txn-last-updated">
                    {(() => {
                      const h = detail.status_history || [];
                      const last = h.length ? h[h.length - 1] : null;
                      if (last) return `Updated ${fmtDate(last.at)}${last.by ? ` · ${last.by}` : ""}`;
                      if (detail.updated_at) return `Updated ${fmtDate(detail.updated_at)}`;
                      return "—";
                    })()}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" data-testid="edit-txn-btn" onClick={() => { setEditing(detail); setFormOpen(true); }}>Edit</Button>
                  {!detail.direct_sale && <PoApprovalActions po={detail} onChanged={() => openDetail(detail.id)} />}
                  {(detail.approval_status || "approved") === "approved"
                    ? <PrintButton path={`pdf/purchase-order/${detail.id}`} testid="txn-po-pdf-btn" className="inline-flex h-8 items-center rounded-sm border border-gray-200 px-3 text-sm font-medium hover:bg-gray-50">Purchase Order</PrintButton>
                    : <Button size="sm" variant="outline" disabled title="Needs Finance and Ronick approval first" data-testid="txn-po-pdf-locked">Purchase Order (awaiting approval)</Button>}
                  <PrintButton path={`pdf/transaction-card/${detail.id}`} testid="txn-card-pdf-btn" className="inline-flex h-8 items-center rounded-sm border border-gray-200 px-3 text-sm font-medium hover:bg-gray-50">Transaction Card</PrintButton>
                  {!detail.direct_sale && <RequestAdvanceButton type="Prepayment" partyId={detail.supplier_id} poId={detail.id} poNo={detail.lx_po_no} currency={detail.currency || "TZS"} defaultAmount={detail.payable_amount || detail.total_amount} testid="txn-request-advance" />}
                  <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="delete-txn-btn" onClick={() => { setDelReason(""); setDelOpen(true); }}>Delete</Button>
                </div>
              </div>
              {!detail.direct_sale && <PoApprovalBanner po={detail} />}

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList data-testid="txn-detail-tabs">
                  <TabsTrigger value="overview" data-testid="txn-tab-overview">Overview</TabsTrigger>
                  <TabsTrigger value="lines" data-testid="txn-tab-lines">Line Items</TabsTrigger>
                  <TabsTrigger value="fulfillment" data-testid="txn-tab-fulfillment">Fulfillment</TabsTrigger>
                  <TabsTrigger value="documents" data-testid="txn-tab-documents">Documents ({(detail.documents || []).length})</TabsTrigger>
                  <TabsTrigger value="timeline" data-testid="txn-tab-timeline">Timeline</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="mt-5 space-y-5">
                  <SectionCard title="Parties & Terms" testid="txn-card-parties">
                    <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-3 lg:grid-cols-4">
                      <Field label="Supplier" value={detail.supplier_name} />
                      <Field label={(detail.client_names || []).length > 1 ? "Clients (per line)" : "Client"} value={(detail.client_names || []).join(", ") || detail.client_name} />
                      <Field label="Currency" value={detail.currency} mono />
                      <Field label="Assigned To" value={detail.assigned_to_name} />
                      <Field label="ETA" value={fmtDate(detail.eta)} mono />
                      <Field label="Holds Stock" value={detail.holds_stock ? "Yes" : "No"} />
                    </div>
                  </SectionCard>

                  <SectionCard title="Amounts" testid="txn-card-amounts">
                    <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-3 lg:grid-cols-4">
                      <Field label="Exclusive" value={fmtNum(detail.exclusive_total, detail.currency)} mono />
                      <Field label={`VAT (${detail.vat_rate}%)`} value={fmtNum(detail.vat_amount, detail.currency)} mono />
                      <Field label="Total" value={fmtNum(detail.total_amount, detail.currency)} mono />
                      {detail.supplier_id
                        ? <Field label="Payable" value={fmtNum(detail.payable_amount, detail.currency)} mono />
                        : <Field label="Client Invoice Value" value={fmtNum(detail.total_amount, detail.currency)} mono />}
                      {detail.currency !== "TZS" && detail.tzs_total_amount != null && (
                        <Field label={`TZS Equiv @ ${fmtNum(detail.exchange_rate)}`} value={fmtNum(detail.tzs_total_amount, "TZS")} mono />
                      )}
                      {(() => {
                        const direct = !!detail.direct_sale;
                        let v = 0, z = 0, e = 0;
                        (detail.line_items || []).forEach((li) => {
                          if (li.cost_only) return;
                          if (!direct && li.stocking) return;
                          const amt = Number(li.exclusive_amount) || 0;
                          const tt = String(li.resolved_tax_type || li.tax_type || "Vatable").toLowerCase();
                          if (tt.includes("exempt")) e += amt; else if (tt.includes("zero")) z += amt; else v += amt;
                        });
                        return (<>
                          <Field label="Vatable (Excl.)" value={fmtNum(v, detail.currency)} mono testid="vat-breakdown-vatable" />
                          {z > 0 && <Field label="Zero-Rated (Excl.)" value={fmtNum(z, detail.currency)} mono testid="vat-breakdown-zero" />}
                          {e > 0 && <Field label="Exempt (Excl.)" value={fmtNum(e, detail.currency)} mono testid="vat-breakdown-exempt" />}
                        </>);
                      })()}
                    </div>
                  </SectionCard>

                  {detail.is_import && [detail.bl_awb_no, detail.commercial_invoice_no, detail.freight_charges, detail.customs_duty].some((v) => v != null && v !== "") && (
                    <SectionCard title="Import Details" testid="txn-card-import">
                      <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
                        <Field label="BL/AWB No" value={detail.bl_awb_no} mono />
                        <Field label="Commercial Inv" value={detail.commercial_invoice_no} mono />
                        <Field label="Freight" value={fmtNum(detail.freight_charges, "TZS")} mono />
                        <Field label="Customs Duty" value={fmtNum(detail.customs_duty, "TZS")} mono />
                      </div>
                    </SectionCard>
                  )}
                </TabsContent>

                <TabsContent value="lines" className="mt-5">
                  <SectionCard title="Line Items" testid="txn-card-lines">
                    <table className="w-full text-sm">
                      <thead><tr className="lx-label border-b border-gray-100 text-gray-400">
                        <th className="py-1 text-left">Description</th><th className="py-1 text-right">Qty</th>
                        <th className="py-1 text-right">Unit</th><th className="py-1 text-center">Tax</th><th className="py-1 text-right">Exclusive</th>
                      </tr></thead>
                      <tbody>
                        {(detail.line_items || []).map((li, i) => (
                          <tr key={i} className="border-b border-gray-50">
                            <td className="py-1">{li.description}</td>
                            <td className="py-1 text-right lx-num">{li.qty}</td>
                            <td className="py-1 text-right lx-num">{fmtNum(li.unit_price)}</td>
                            <td className="py-1 text-center text-xs text-gray-500">{li.resolved_tax_type || li.tax_type || "Vatable"}</td>
                            <td className="py-1 text-right lx-num">{fmtNum(li.exclusive_amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </SectionCard>
                </TabsContent>

                <TabsContent value="fulfillment" className="mt-5 space-y-5">
                  {(fulStats || (adjStats && ((adjStats.credit_notes?.length > 0) || (adjStats.debit_notes?.length > 0)))) && (
                    <SectionCard title="Fulfillment Summary" testid="txn-card-ful-summary">
                      <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-3 lg:grid-cols-4">
                        {fulStats && <Field label="Total GP" value={fmtNum(fulStats.total_gp, "TZS")} mono />}
                        {adjStats && (adjStats.credit_notes?.length > 0) && (
                          <Field label={`Net Credited · Client (${adjStats.credit_notes.length})`} testid="adj-net-credited"
                            value={fmtNum(adjStats.credit_notes.reduce((s, n) => s + (n.total_amount || 0), 0), detail.currency)} mono />
                        )}
                        {adjStats && (adjStats.debit_notes?.length > 0) && (
                          <Field label={`Net Debited · Supplier (${adjStats.debit_notes.length})`} testid="adj-net-debited"
                            value={fmtNum(adjStats.debit_notes.reduce((s, n) => s + (n.total_amount || 0), 0), detail.currency)} mono />
                        )}
                      </div>
                    </SectionCard>
                  )}
                  <SectionCard testid="txn-card-fulfillment">
                    {detail.is_import && <div className="mb-6"><ImportDocuments transaction={detail} onChanged={() => openDetail(detail.id)} /></div>}
                    <Fulfillment transaction={detail} onStats={setFulStats} onAdjustments={setAdjStats} onChanged={() => { openDetail(detail.id); load(); }} />
                  </SectionCard>
                </TabsContent>

                <TabsContent value="documents" className="mt-5">
                  <SectionCard title={`Linked Documents (${(detail.documents || []).length})`} testid="txn-card-documents"
                    actions={
                      <label className="inline-flex cursor-pointer items-center gap-1 rounded-sm bg-gray-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-gray-700" data-testid="txn-upload-doc">
                        {uploadingDoc ? "Uploading…" : "Attach document"}
                        <input type="file" className="hidden" accept="image/*,application/pdf" onChange={(e) => { uploadDoc(e.target.files[0]); e.target.value = ""; }} />
                      </label>
                    }>
                    {(detail.documents || []).length === 0 && <div className="text-sm text-gray-400">None linked. Attach a PDF or image using the button above.</div>}
                    {(detail.documents || []).map((d) => (
                      <div key={d.id} className="flex items-center justify-between border-b border-gray-50 py-1.5 text-sm" data-testid={`linked-doc-${d.id}`}>
                        <span className="truncate">{d.original_filename}</span>
                        <span className="flex shrink-0 items-center gap-2">
                          <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 text-xs">{d.document_type?.replace(/_/g, " ")}</span>
                          <button className="text-xs font-medium text-[#122E5C] hover:underline" data-testid={`preview-doc-${d.id}`} onClick={() => previewDoc(d)}>Preview</button>
                        </span>
                      </div>
                    ))}
                  </SectionCard>
                </TabsContent>

                <TabsContent value="timeline" className="mt-5">
                  <SectionCard title="Status Timeline" testid="txn-card-timeline">
                    <div className="space-y-2">
                      {(detail.status_history || []).map((h, i) => (
                        <div key={i} className="flex items-start gap-3 text-sm">
                          <div className="mt-1 h-2 w-2 rounded-full bg-[#1B4383]" />
                          <div>
                            <div className="font-medium">{h.status}</div>
                            <div className="text-xs text-gray-500">{fmtDate(h.at)} · {h.by}{h.note ? ` · ${h.note}` : ""}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </SectionCard>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>

        <TransactionForm open={formOpen} onOpenChange={setFormOpen} initial={editing}
          clients={clients} suppliers={suppliers} users={users}
          onSaved={() => { if (detail) openDetail(detail.id); }} />

        <Dialog open={delOpen} onOpenChange={(v) => { setDelOpen(v); if (!v) setDelReason(""); }}>
          <DialogContent className="max-w-lg" data-testid="delete-reason-dialog">
            <DialogHeader><DialogTitle className="font-display">Delete Somcore PO</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-gray-600">This moves <span className="font-semibold">{detail?.lx_po_no || "this PO"}</span> to Deleted Items. A reason is required for the audit trail.</p>
              <div>
                <Label className="lx-label">Reason for deletion</Label>
                <Input className="mt-1" data-testid="delete-reason-input" placeholder="e.g. Duplicate entry, cancelled order…" value={delReason} onChange={(e) => setDelReason(e.target.value)} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDelOpen(false)}>Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700" data-testid="delete-reason-confirm" onClick={doDelete}>Delete PO</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!preview} onOpenChange={(v) => { if (!v) setPreview(null); }}>
          <DialogContent className="max-w-4xl p-0" data-testid="doc-preview-dialog">
            <DialogHeader className="border-b border-gray-200 px-6 py-4">
              <DialogTitle className="font-display truncate">{preview?.doc?.original_filename}</DialogTitle>
            </DialogHeader>
            <div className="flex items-center justify-center bg-gray-100 p-4" style={{ height: "72vh" }}>
              {!preview?.url ? (
                <div className="text-sm text-gray-400">Loading preview…</div>
              ) : preview.doc.content_type?.includes("pdf") ? (
                <iframe title="doc-preview" src={preview.url} className="h-full w-full" data-testid="doc-preview-frame" />
              ) : (
                <img src={preview.url} alt={preview.doc.original_filename} className="max-h-full max-w-full object-contain" data-testid="doc-preview-img" />
              )}
            </div>
          </DialogContent>
        </Dialog>
      </Layout>
    );
  }

  const displayRows = statusSel.length ? rows.filter((t) => statusSel.includes(t.status)) : rows;
  const openRows = displayRows.filter((t) => t.status !== "Closed");
  const openValue = openRows.reduce((s, t) => s + (t.tzs_total_amount || 0), 0);
  const RECEIVED = ["Received", "Partially Delivered", "Delivered", "Partially Invoiced", "Invoiced by Supplier", "Completed"];
  const inTransitRows = openRows.filter((t) => t.is_import && !RECEIVED.includes(t.fulfillment_status));
  const inTransitValue = inTransitRows.reduce((s, t) => s + (t.tzs_total_amount || 0), 0);
  const todayStr = new Date().toISOString().slice(0, 10);
  const overdueRows = openRows.filter((t) => t.eta && String(t.eta).slice(0, 10) < todayStr);
  const overdueValue = overdueRows.reduce((s, t) => s + (t.tzs_total_amount || 0), 0);

  return (
    <Layout>
      <PageHeader
        title="Somcore PO"
        subtitle={statusSel.length ? `${displayRows.length} of ${rows.length} records` : `${rows.length} records`}
        actions={
          <>
            <Button variant="outline" data-testid="export-transactions" onClick={() => exportToCsv("transactions", rows, cols)}><DownloadSimple size={16} className="mr-1" />Export</Button>
            <Button variant="outline" data-testid="from-client-po-btn" onClick={() => setPocOpen(true)}>From Client PO</Button>
            <Button variant="outline" data-testid="from-stocking-btn" onClick={() => { setEditing({ holds_stock: true, status: "PO Placed", currency: "TZS", vat_rate: 18, is_import: false }); setFormOpen(true); }}>From Stocking</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="new-transaction-btn" onClick={() => navigate("/transactions/new")}><Plus size={16} className="mr-1" />New Somcore PO</Button>
          </>
        }
      />
      <div className="p-8">
        <KpiStrip cols={3}>
          <KpiCard icon={Coins} label="Open PO Value" value={`TSh ${fmtNum(openValue)}`} sub={`${openRows.length} open POs`} testid="kpi-txn-open-value" />
          <KpiCard icon={Truck} label="In-Transit Value" value={`TSh ${fmtNum(inTransitValue)}`} sub={`${inTransitRows.length} imports en route`} tone="info" testid="kpi-txn-intransit-value" />
          <KpiCard icon={Warning} label="Overdue (past ETA)" value={overdueRows.length} sub={`TSh ${fmtNum(overdueValue)} past ETA`} tone={overdueRows.length ? "warning" : "default"} testid="kpi-txn-overdue" />
        </KpiStrip>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-2.5 top-2.5 text-gray-400" />
            <Input data-testid="txn-search" placeholder="Search PO / invoice…" className="w-64 pl-8" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
          </div>
          <MultiSelectFilter label="Status" options={STATUS_FLOW} selected={statusSel} onChange={setStatusSel} testid="txn-filter-status" width="w-52" />
          <Select value={filters.currency} onValueChange={(v) => setFilters({ ...filters, currency: v })}>
            <SelectTrigger className="w-36" data-testid="txn-filter-currency"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All currency</SelectItem>
              {currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}
            </SelectContent>
          </Select>
          {(draftSummary.total > 0 || isFinance) && (
            <div className="ml-auto flex items-center gap-2" data-testid="drafts-summary">
              {draftSummary.total > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-800" data-testid="drafts-total-badge">
                  {draftSummary.total} unposted supplier-invoice draft{draftSummary.total === 1 ? "" : "s"}
                  {draftSummary.stale_total > 0 ? <span className="rounded-full bg-amber-800 px-1.5 py-0.5 text-[10px] text-white" data-testid="drafts-stale-badge">{draftSummary.stale_total} over {draftSummary.stale_days}d</span> : null}
                </span>
              )}
              {isFinance && <button className="text-xs text-gray-500 underline hover:text-[#1B4383]" data-testid="drafts-threshold-btn" onClick={() => { setThreshVal(String(draftSummary.stale_days)); setThreshOpen(true); }}>Reminder window: {draftSummary.stale_days}d</button>}
            </div>
          )}
        </div>

        <div className="max-h-[calc(100vh-15rem)] overflow-auto rounded-sm border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50"><tr className="lx-label border-b border-gray-200">
              <th className="sticky left-0 z-20 bg-gray-50 px-4 py-2 text-left">Somcore PO</th>
              <th className="px-4 py-2 text-left">Supplier</th>
              <th className="px-4 py-2 text-left">Client</th>
              <th className="px-4 py-2 text-left">Client PO</th>
              <th className="px-4 py-2 text-right">Total</th>
              <th className="px-4 py-2 text-right">Payable</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Fulfillment</th>
              <th className="px-4 py-2 text-left">Assigned</th>
              <th className="px-4 py-2 text-left">ETA</th>
            </tr></thead>
            <tbody>
              {displayRows.length === 0 && <TableEmpty colSpan={10} title="No Somcore POs match" message="No Somcore POs match the current filters. Adjust the status filter or search to see more." testid="txn-empty" />}
              {displayRows.map((t) => (
                <tr key={t.id} data-testid={`txn-row-${t.id}`} className="cursor-pointer border-b border-gray-100 hover:bg-gray-50" onClick={() => navigate(`/transactions/${t.id}`)}>
                  <td className="sticky left-0 bg-white px-4 py-2 font-mono-num font-medium">
                    {t.lx_po_no || "—"}
                    {t.is_import && (() => { const c = importChecklist(t.import_details); return <span className="ml-2 rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700" data-testid={`import-badge-${t.id}`}>{c.captured}/{c.total} docs</span>; })()}
                    {draftSummary.by_transaction?.[t.id] ? <span className="ml-2 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700" data-testid={`draft-badge-${t.id}`} title="Unposted supplier-invoice draft(s)">{draftSummary.by_transaction[t.id]} draft{draftSummary.by_transaction[t.id] === 1 ? "" : "s"}</span> : null}
                  </td>
                  <td className="px-4 py-2">{t.supplier_name || "—"}</td>
                  <td className="px-4 py-2" data-testid={`txn-clients-${t.id}`}>{(t.client_names || []).length ? t.client_names.join(", ") : (t.client_name || "—")}</td>
                  <td className="px-4 py-2" data-testid={`txn-client-po-${t.id}`}>{(t.client_po_nos || []).length ? <span className="font-mono-num">{t.client_po_nos.join(", ")}</span> : <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 text-xs text-gray-400">Not Linked</span>}</td>
                  <td className="px-4 py-2 text-right lx-num">{curSym(t.currency)} {fmtNum(t.total_amount)}</td>
                  <td className="px-4 py-2 text-right lx-num">{t.supplier_id ? fmtNum(t.payable_amount, t.currency) : "—"}</td>
                  <td className="px-4 py-2"><div className="flex flex-wrap items-center gap-1"><StatusBadge status={t.status} />{!t.direct_sale && (t.approval_status || "approved") !== "approved" && <PoApprovalPill status={t.approval_status} testid={`txn-approval-pill-${t.id}`} />}</div></td>
                  <td className="px-4 py-2" data-testid={`txn-fulfillment-${t.id}`}><FulfillmentPill status={t.fulfillment_status} /></td>
                  <td className="px-4 py-2">{t.assigned_to_name || "—"}</td>
                  <td className="px-4 py-2 lx-num">{fmtDate(t.eta)}{t.eta && String(t.eta).slice(0, 10) < new Date().toISOString().slice(0, 10) && !["Delivered", "Invoiced", "Reconciled", "Closed"].includes(t.status) && <span className="ml-1 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700" data-testid={`past-eta-${t.id}`}>past ETA</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <TransactionForm
        open={formOpen} onOpenChange={setFormOpen} initial={editing}
        clients={clients} suppliers={suppliers} users={users}
        onSaved={(saved) => { load(); if (saved?.direct_sale && saved?.id) { setEditing(null); navigate(`/transactions/${saved.id}`); } }}
      />
      <UploadDialog open={pocOpen} onOpenChange={setPocOpen} existingDoc={null} presetType="Purchase_Order" forceCreate onDone={load} />
      <Dialog open={threshOpen} onOpenChange={setThreshOpen}>
        <DialogContent className="max-w-sm" data-testid="drafts-threshold-dialog">
          <DialogHeader><DialogTitle className="font-display">Draft reminder window</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">Finance is nudged (in-app + a daily email digest) about supplier-invoice drafts left unposted for at least this many days.</p>
            <div>
              <Label className="lx-label">Stale after (days)</Label>
              <Input type="number" min="1" max="90" className="mt-1 w-32" data-testid="drafts-threshold-input" value={threshVal} onChange={(e) => setThreshVal(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setThreshOpen(false)}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="drafts-threshold-save" onClick={saveThreshold}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function Field({ label, value, mono, testid }) {
  return (
    <div data-testid={testid}>
      <div className="lx-label">{label}</div>
      <div className={mono ? "lx-num" : ""}>{value ?? "—"}</div>
    </div>
  );
}
